import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import Iconify from "src/components/iconify";
import flashFill from "@iconify/icons-eva/flash-fill";
import closeFill from "@iconify/icons-eva/close-fill";
import alertTriangleFill from "@iconify/icons-eva/alert-triangle-fill";
import arrowBackFill from "@iconify/icons-eva/arrow-back-fill";
import PropTypes from "prop-types";
import { BRAND } from "src/theme/brandTokens";
const DANGER = "#D32F2F";

/**
 * Dialog que muestra el prompt original de generación con IA y permite
 * regenerar la propuesta con un prompt editado. Flujo inline en 2 pasos:
 * primero edición del prompt, luego confirmación de sobrescritura.
 *
 * El parent maneja la API call (`useGenerarConIAMutation`) y decide qué
 * hacer con el borrador resultante (típicamente: aplicarBorrador + setear
 * promptOriginal). El dialog solo se ocupa del flujo UX y del loading state.
 */
const RegenerarIADialog = ({
  open,
  onClose,
  promptOriginal,
  onRegenerar,
  isLoading,
}) => {
  const [paso, setPaso] = useState("editar");
  const [prompt, setPrompt] = useState("");

  // Reset al abrir: vuelve al paso editar y carga el prompt original.
  useEffect(() => {
    if (open) {
      setPaso("editar");
      setPrompt(promptOriginal || "");
    }
  }, [open, promptOriginal]);

  // Backdrop y Esc bloqueados durante confirmación o carga para evitar
  // cancelar la API call a mitad o dejar la propuesta a medio regenerar.
  const handleClose = (_event, reason) => {
    if (paso === "confirmar" || isLoading) return;
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      onClose();
      return;
    }
    onClose();
  };

  const irAConfirmar = () => setPaso("confirmar");
  const volverAEditar = () => setPaso("editar");

  const confirmarRegenerar = () => {
    onRegenerar(prompt.trim());
  };

  const promptVacio = prompt.trim() === "";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      {paso === "editar" && (
        <>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Iconify
                icon={flashFill}
                width={20}
                sx={{ color: BRAND.color.primary }}
              />
              <Typography variant="h6">Prompt original</Typography>
            </Box>
            <IconButton size="small" onClick={onClose} disabled={isLoading}>
              <Iconify icon={closeFill} width={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Edita el prompt para regenerar la propuesta con IA. Esto
              sobrescribirá los campos generados.
            </Typography>
            <TextField
              autoFocus
              multiline
              minRows={8}
              fullWidth
              variant="outlined"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={isLoading}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit" disabled={isLoading}>
              Cancelar
            </Button>
            <Button
              onClick={irAConfirmar}
              variant="contained"
              disabled={promptVacio || isLoading}
              startIcon={<Iconify icon={flashFill} width={16} />}
              sx={{
                bgcolor: BRAND.color.primary,
                "&:hover": { bgcolor: BRAND.color.primaryDark },
              }}
            >
              Regenerar IA
            </Button>
          </DialogActions>
        </>
      )}

      {paso === "confirmar" && (
        <>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Iconify
              icon={alertTriangleFill}
              width={20}
              sx={{ color: DANGER }}
            />
            <Typography variant="h6">¿Regenerar la propuesta?</Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                bgcolor: "#FFEBEE",
                borderLeft: `4px solid ${DANGER}`,
                p: 1.5,
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#B71C1C", fontWeight: 600 }}
              >
                Esto sobrescribirá todos los campos generados por IA, incluyendo
                tus ediciones manuales.
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              Se regenerarán: título, descripción, antecedentes, alcance,
              próximos pasos, condiciones de pago, duración estimada y todas las
              fases/servicios.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={volverAEditar}
              color="inherit"
              disabled={isLoading}
              startIcon={<Iconify icon={arrowBackFill} width={16} />}
            >
              Volver
            </Button>
            <Button
              onClick={confirmarRegenerar}
              variant="contained"
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <Iconify icon={flashFill} width={16} />
                )
              }
              sx={{ bgcolor: DANGER, "&:hover": { bgcolor: "#B71C1C" } }}
            >
              {isLoading ? "Regenerando..." : "Sí, regenerar"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

RegenerarIADialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  promptOriginal: PropTypes.string.isRequired,
  onRegenerar: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

RegenerarIADialog.defaultProps = {
  isLoading: false,
};

export default RegenerarIADialog;
